import { FC, useContext, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  Tab,
  Tabs,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Chip,
  CircularProgress,
} from '@mui/material';
import { tokens } from '../../theme';
import { FilterContext } from '../../contexts/FilterContext';
import {
  fetchWebinarRegistrationRate,
  fetchWebinarRegistrationTime,
  fetchWebinarRegistrationDistribution,
  fetchProductRegistrations,
  fetchWebinarRegistrationTimeline,
  fetchWebinarPerformance,
  fetchWebinarRegistrationDetails,
  fetchProductRegistrationDetails,
  computeWebinarRegistrationTimeSummary,
  WebinarRegistrationRateData,
  WebinarRegistrationTimeData,
  WebinarRegistrationDistributionData,
  ProductRegistrationData,
  WebinarRegistrationTimelineData,
  WebinarPerformanceData,
  WebinarRegistrationDetailData,
} from '../../helpers/webinarStatisticsData';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import { Close, InfoOutlined } from '@mui/icons-material';
import Header from '../../components/Header';
import moment from 'moment';

// Timezone-safe date formatter for URL parameters
const formatDateForURL = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const WebinarUserVerhalten: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState, setFilterState } = useContext(FilterContext)!;

  // Client filtering
  // 'wh' entfernt 2026-09-09: der Client ist stillgelegt, seine Zeilen in mc_members,
  // mc_member_tags und mc_tag_meta sind entfernt (Sicherung unter
  // untracked/wh-decommission-20260909/). 'am' steht seit seiner Stilllegung am
  // 2026-05-27 im selben Zustand und bleibt vorerst drin — bewusst nicht mitgeaendert.
  const clientOptions = ['ft', 'am', 'be', 'dnv', 'fp', 'gp', 'sw', 'tt'];
  const [selectedClients, setSelectedClients] = useState<string[]>(['ft']);

  const [tabValue, setTabValue] = useState(0);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [registrationRateData, setRegistrationRateData] =
    useState<WebinarRegistrationRateData | null>(null);
  const [registrationTimeData, setRegistrationTimeData] = useState<WebinarRegistrationTimeData[]>(
    [],
  );
  const [registrationDistributionData, setRegistrationDistributionData] = useState<
    WebinarRegistrationDistributionData[]
  >([]);
  const [productRegistrationData, setProductRegistrationData] = useState<ProductRegistrationData[]>(
    [],
  );
  const [timelineData, setTimelineData] = useState<WebinarRegistrationTimelineData[]>([]);
  const [webinarPerformanceData, setWebinarPerformanceData] = useState<WebinarPerformanceData[]>(
    [],
  );

  // Detail dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailData, setDetailData] = useState<WebinarRegistrationDetailData[]>([]);
  const [detailTitle, setDetailTitle] = useState('');

  const [loading, setLoading] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const startDate = filterState.startDate?.toISOString().split('T')[0];
      const endDate = filterState.endDate?.toISOString().split('T')[0];
      const clients = selectedClients;

      const [rateData, timeData, distributionData, productData, timeline, performanceData] =
        await Promise.all([
          fetchWebinarRegistrationRate(startDate, endDate, clients),
          fetchWebinarRegistrationTime(startDate, endDate, clients),
          fetchWebinarRegistrationDistribution(startDate, endDate, clients),
          fetchProductRegistrations(startDate, endDate, clients),
          fetchWebinarRegistrationTimeline(startDate, endDate, clients),
          fetchWebinarPerformance(startDate, endDate, clients),
        ]);

      setRegistrationRateData(rateData);
      setRegistrationTimeData(timeData);
      setRegistrationDistributionData(distributionData);
      setProductRegistrationData(productData);
      setTimelineData(timeline);
      setWebinarPerformanceData(performanceData);
    } catch (error) {
      console.error('Error loading webinar statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize from URL parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const updates: any = {};

    // Read startDate from URL
    const startDateParam = urlParams.get('startDate');
    if (startDateParam) {
      const parsedDate = moment(startDateParam, 'YYYY-MM-DD', true);
      if (parsedDate.isValid()) {
        updates.startDate = parsedDate.toDate();
      }
    }

    // Read endDate from URL
    const endDateParam = urlParams.get('endDate');
    if (endDateParam) {
      const parsedDate = moment(endDateParam, 'YYYY-MM-DD', true);
      if (parsedDate.isValid()) {
        updates.endDate = parsedDate.toDate();
      }
    }

    // Read clients from URL
    const clientsParam = urlParams.get('clients');
    if (clientsParam) {
      const clientArray = clientsParam.split(',').filter((c) => c.trim());
      if (clientArray.length > 0) {
        setSelectedClients(clientArray);
      }
    }

    // Read tab from URL
    const tabParam = urlParams.get('tab');
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 4) {
        setTabValue(tabIndex);
      }
    }

    // Update filter state with URL parameters
    if (updates.startDate || updates.endDate) {
      setFilterState((prev) => ({
        ...prev,
        ...(updates.startDate && { startDate: updates.startDate }),
        ...(updates.endDate && { endDate: updates.endDate }),
      }));
    }

    // Mark initialization as complete and trigger initial data load
    setTimeout(() => {
      setIsInitialMount(false);
    }, 0);
  }, []); // Run only once on mount

  // Sync URL with filter state changes
  useEffect(() => {
    // Skip URL sync on initial mount to prevent overwriting URL parameters
    if (isInitialMount) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);

    // Update date parameters
    if (filterState.startDate) {
      urlParams.set('startDate', formatDateForURL(filterState.startDate));
    } else {
      urlParams.delete('startDate');
    }

    if (filterState.endDate) {
      urlParams.set('endDate', formatDateForURL(filterState.endDate));
    } else {
      urlParams.delete('endDate');
    }

    // Update clients parameter
    if (selectedClients.length > 0) {
      urlParams.set('clients', selectedClients.join(','));
    } else {
      urlParams.delete('clients');
    }

    // Update tab parameter
    if (tabValue > 0) {
      urlParams.set('tab', tabValue.toString());
    } else {
      urlParams.delete('tab');
    }

    // Update URL without adding to browser history
    const newUrl = urlParams.toString()
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [filterState.startDate, filterState.endDate, selectedClients, tabValue, isInitialMount]);

  // Load data when filters change
  useEffect(() => {
    // Skip data loading on initial mount to prevent loading with empty filters
    if (isInitialMount) {
      return;
    }

    if (filterState.startDate && filterState.endDate) {
      loadData();
    }
  }, [filterState.startDate, filterState.endDate, selectedClients, isInitialMount]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle row clicks for detail views
  const handleDistributionRowClick = async (rowData: unknown) => {
    const row = rowData as WebinarRegistrationDistributionData;
    if (row.time_bucket === 'Nicht registriert') return; // Skip non-clickable rows

    try {
      const startDate = filterState.startDate?.toISOString().split('T')[0];
      const endDate = filterState.endDate?.toISOString().split('T')[0];
      const clients = selectedClients;

      const details = await fetchWebinarRegistrationDetails(
        row.time_bucket,
        startDate,
        endDate,
        clients,
      );
      setDetailData(details);
      setDetailTitle(`Registrierungsdetails: ${row.time_bucket}`);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Error loading registration details:', error);
    }
  };

  const handleProductRowClick = async (rowData: unknown) => {
    const row = rowData as ProductRegistrationData;

    try {
      const startDate = filterState.startDate?.toISOString().split('T')[0];
      const endDate = filterState.endDate?.toISOString().split('T')[0];
      const clients = selectedClients;

      const details = await fetchProductRegistrationDetails(
        row.product_code,
        startDate,
        endDate,
        clients,
      );
      setDetailData(details);
      setDetailTitle(`Produktregistrierungen: ${row.product_name}`);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Error loading product registration details:', error);
    }
  };

  // Summary calculations
  const registrationTimeSummary = computeWebinarRegistrationTimeSummary(registrationTimeData);

  // Chart data preparation with normalized dual-axis approach
  const chartData =
    timelineData.length > 0
      ? (() => {
          // Find max values for normalization
          const maxSignups = Math.max(...timelineData.map((item) => item.new_signups));
          const maxRegistrations = Math.max(...timelineData.map((item) => item.registrations));

          // Normalize registrations to signup scale (multiply by factor to make it visible)
          const normalizationFactor = (maxSignups / maxRegistrations) * 0.3; // Scale to 30% of signup range

          return [
            {
              id: 'Anmeldungen',
              data: timelineData.map((item) => ({
                x: item.month,
                y: item.new_signups,
                originalValue: item.new_signups,
              })),
            },
            {
              id: 'Webinar-Registrierungen',
              data: timelineData.map((item) => ({
                x: item.month,
                y: item.registrations * normalizationFactor,
                originalValue: item.registrations,
              })),
            },
          ];
        })()
      : [];

  return (
    <Box m="20px">
      <Header
        title="Webinar User Verhalten"
        subtitle="Analyse des E-Mail- und Registrierungsverhaltens für StealthSeminar Webinare"
      />

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          {/* Info Box */}
          <Paper
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: colors.blueAccent[800],
              border: `1px solid ${colors.blueAccent[600]}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <InfoOutlined sx={{ color: colors.blueAccent[400], fontSize: 24, mt: 0.5 }} />
              <Box>
                <Typography variant="h6" sx={{ color: colors.blueAccent[400], mb: 1 }}>
                  Webinar-Registrierungs-Analyse: Anmeldungs-basierte Auswertung
                </Typography>
                <Typography variant="body2" sx={{ color: colors.grey[100], lineHeight: 1.6 }}>
                  <strong>
                    Diese Statistik analysiert die Webinar-Teilnahme von Erstanmeldungen:
                  </strong>
                  <br />• <strong>Zeitraum-Filter bezieht sich auf Anmeldedatum</strong> (nicht
                  Webinar-Registrierungsdatum)
                  <br />• <strong>Webinar-Registrierungen:</strong> Nutzer, die sich im Zeitraum
                  angemeldet haben UND seitdem für Webinare registriert haben
                  <br />• <strong>Nur StealthSeminar Webinare:</strong> Diese Auswertung zeigt
                  ausschließlich Daten von StealthSeminar-basierten Webinaren
                  <br />• <strong>Beispiel:</strong> Registrierte E-Mails = Personen meldeten sich
                  im letzten Jahr an und registrierten sich seitdem (egal wann) für mindestens ein
                  Webinar
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Filter Controls */}
          <Box mb={3} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="client-select-label">Client</InputLabel>
              <Select
                labelId="client-select-label"
                id="client-select"
                multiple
                value={selectedClients}
                onChange={(event) => {
                  const value =
                    typeof event.target.value === 'string'
                      ? event.target.value.split(',')
                      : event.target.value;
                  setSelectedClients(value);
                }}
                input={<OutlinedInput id="select-multiple-client-chip" label="Client" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value.toUpperCase()} size="small" />
                    ))}
                  </Box>
                )}
              >
                {clientOptions.map((client) => (
                  <MenuItem key={client} value={client}>
                    {client.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab label="Übersicht" />
            <Tab label="Zeitbasierte Analyse" />
            <Tab label="Produkt-Analyse" />
            <Tab label="Webinar-Performance" />
            <Tab label="Timeline" />
          </Tabs>

          {/* Tab 0: Overview */}
          {tabValue === 0 && (
            <Box>
              {/* Summary Cards */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '20px',
                  mb: '20px',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: colors.primary[400],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    p: '20px',
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Tooltip title="Gesamtanzahl der E-Mail-Adressen, die sich im gewählten Zeitraum erstmalig angemeldet haben">
                      <Typography variant="h4" fontWeight="bold" sx={{ color: colors.grey[100] }}>
                        {registrationRateData?.total_emails?.toLocaleString() || 0}
                      </Typography>
                    </Tooltip>
                    <Typography color={colors.greenAccent[500]}>Gesamt-Anmeldungen</Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    backgroundColor: colors.primary[400],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    p: '20px',
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Tooltip title="Anzahl der E-Mail-Adressen aus dem gewählten Zeitraum, die sich für mindestens ein Webinar registriert haben">
                      <Typography variant="h4" fontWeight="bold" sx={{ color: colors.grey[100] }}>
                        {registrationRateData?.registered_emails?.toLocaleString() || 0}
                      </Typography>
                    </Tooltip>
                    <Typography color={colors.greenAccent[500]}>
                      Unique Webinar-Registrierungen
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    backgroundColor: colors.primary[400],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    p: '20px',
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Tooltip title="Gesamtanzahl aller Webinar-Registrierungen (eine Person kann sich für mehrere Webinare registrieren)">
                      <Typography variant="h4" fontWeight="bold" sx={{ color: colors.grey[100] }}>
                        {registrationRateData?.total_registrations?.toLocaleString() || 0}
                      </Typography>
                    </Tooltip>
                    <Typography color={colors.greenAccent[500]}>Gesamt-Registrierungen</Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    backgroundColor: colors.primary[400],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    p: '20px',
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Tooltip title="Anteil der E-Mail-Adressen aus dem gewählten Zeitraum, die sich für mindestens ein Webinar registriert haben">
                      <Typography variant="h4" fontWeight="bold" sx={{ color: colors.grey[100] }}>
                        {registrationRateData?.registration_rate || 0}%
                      </Typography>
                    </Tooltip>
                    <Typography color={colors.greenAccent[500]}>Registrierungsrate</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Registration Time Summary */}
              <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
                <Typography variant="h6" sx={{ color: colors.grey[100], mb: 2 }}>
                  Zeit bis zur ersten Webinar-Registrierung
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ color: colors.greenAccent[500] }}>
                      {registrationTimeSummary.averageDays} Tage
                    </Typography>
                    <Typography color={colors.grey[200]}>Durchschnitt</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ color: colors.greenAccent[500] }}>
                      {registrationTimeSummary.medianDays} Tage
                    </Typography>
                    <Typography color={colors.grey[200]}>Median</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ color: colors.greenAccent[500] }}>
                      {registrationTimeSummary.minDays} Tage
                    </Typography>
                    <Typography color={colors.grey[200]}>Minimum</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ color: colors.greenAccent[500] }}>
                      {registrationTimeSummary.maxDays} Tage
                    </Typography>
                    <Typography color={colors.grey[200]}>Maximum</Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Tab 1: Time-based Analysis */}
          {tabValue === 1 && (
            <Box>
              <Typography variant="h5" sx={{ color: colors.grey[100], mb: 2 }}>
                Zeit zwischen E-Mail-Anmeldung und erster Webinar-Registrierung
              </Typography>

              <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400] }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>Zeitraum</TableCell>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>
                        Anzahl Registrierungen
                      </TableCell>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>Anteil (%)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {registrationDistributionData.map((row) => (
                      <TableRow
                        key={row.time_bucket}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleDistributionRowClick(row)}
                      >
                        <TableCell sx={{ color: 'black' }}>{row.time_bucket}</TableCell>
                        <TableCell sx={{ color: 'black' }}>{row.count}</TableCell>
                        <TableCell sx={{ color: 'black' }}>{row.percentage}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 2: Product Analysis */}
          {tabValue === 2 && (
            <Box>
              <Typography variant="h5" sx={{ color: colors.grey[100], mb: 2 }}>
                Webinar-Registrierungen nach Produkten
              </Typography>

              <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400] }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>
                        Produkt Code
                      </TableCell>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>
                        Produkt Name
                      </TableCell>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>
                        Registrierte E-Mails
                      </TableCell>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>
                        Gesamt-Registrierungen
                      </TableCell>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>
                        Ø Tage bis Registrierung
                      </TableCell>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>
                        Registrierungsrate (%)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productRegistrationData.map((row) => (
                      <TableRow
                        key={row.product_code}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleProductRowClick(row)}
                      >
                        <TableCell sx={{ color: 'black' }}>{row.product_code}</TableCell>
                        <TableCell sx={{ color: 'black' }}>{row.product_name}</TableCell>
                        <TableCell sx={{ color: 'black' }}>{row.registered_emails}</TableCell>
                        <TableCell sx={{ color: 'black' }}>{row.total_registrations}</TableCell>
                        <TableCell sx={{ color: 'black' }}>
                          {row.avg_days_to_register
                            ? parseFloat(String(row.avg_days_to_register)).toFixed(1)
                            : 'N/A'}
                        </TableCell>
                        <TableCell sx={{ color: 'black' }}>{row.registration_rate}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 3: Webinar Performance */}
          {tabValue === 3 && (
            <Box>
              <Typography variant="h5" sx={{ color: colors.grey[100], mb: 2 }}>
                Einzelne Webinar-Performance
              </Typography>

              <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400] }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>Webinar ID</TableCell>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>
                        Webinar Name
                      </TableCell>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>Produkt</TableCell>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>
                        Registrierungen
                      </TableCell>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>
                        Ø Tage bis Registrierung
                      </TableCell>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>
                        Erste Registrierung
                      </TableCell>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>
                        Letzte Registrierung
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {webinarPerformanceData.map((row) => (
                      <TableRow key={row.webinar_id}>
                        <TableCell sx={{ color: 'black' }}>{row.webinar_id}</TableCell>
                        <TableCell sx={{ color: 'black' }}>{row.webinar_name}</TableCell>
                        <TableCell sx={{ color: 'black' }}>{row.product_name}</TableCell>
                        <TableCell sx={{ color: 'black' }}>{row.registrations}</TableCell>
                        <TableCell sx={{ color: 'black' }}>
                          {row.avg_days_to_register
                            ? parseFloat(String(row.avg_days_to_register)).toFixed(1)
                            : 'N/A'}
                        </TableCell>
                        <TableCell sx={{ color: 'black' }}>
                          {new Date(row.first_registration).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ color: 'black' }}>
                          {new Date(row.last_registration).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 4: Timeline */}
          {tabValue === 4 && (
            <Box>
              <Typography variant="h5" sx={{ color: colors.grey[100], mb: 2 }}>
                Monatliche Entwicklung: Anmeldungen vs. Webinar-Registrierungen
              </Typography>

              <Box
                sx={{
                  height: '400px',
                  backgroundColor: colors.primary[400],
                  p: '20px',
                  borderRadius: '8px',
                }}
              >
                <ResponsiveLine
                  data={chartData}
                  margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
                  xScale={{ type: 'point' }}
                  yScale={{
                    type: 'linear',
                    min: 'auto',
                    max: 'auto',
                    stacked: false,
                    reverse: false,
                  }}
                  curve="catmullRom"
                  tooltip={({ point }) => (
                    <div
                      style={{
                        background: colors.primary[400],
                        padding: '9px 12px',
                        border: `1px solid ${colors.grey[100]}`,
                        borderRadius: '4px',
                        color: colors.grey[100],
                      }}
                    >
                      <strong>{point.serieId}</strong>
                      <br />
                      Monat: {point.data.xFormatted}
                      <br />
                      Anzahl:{' '}
                      {(point.data as any).originalValue?.toLocaleString('de-DE') ||
                        point.data.yFormatted}
                      {point.serieId === 'Webinar-Registrierungen' && (
                        <div style={{ fontSize: '0.8em', opacity: 0.8, marginTop: '4px' }}>
                          (Skaliert für Visualisierung)
                        </div>
                      )}
                    </div>
                  )}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: -45,
                    legend: 'Monat',
                    legendOffset: 36,
                    legendPosition: 'middle',
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: 'Anzahl',
                    legendOffset: -40,
                    legendPosition: 'middle',
                  }}
                  pointSize={10}
                  pointColor={{ theme: 'background' }}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  pointLabelYOffset={-12}
                  useMesh={true}
                  legends={[
                    {
                      anchor: 'bottom-right',
                      direction: 'column',
                      justify: false,
                      translateX: 100,
                      translateY: 0,
                      itemsSpacing: 0,
                      itemDirection: 'left-to-right',
                      itemWidth: 120,
                      itemHeight: 20,
                      itemOpacity: 0.75,
                      symbolSize: 12,
                      symbolShape: 'circle',
                      symbolBorderColor: 'rgba(0, 0, 0, .5)',
                      effects: [
                        {
                          on: 'hover',
                          style: {
                            itemBackground: 'rgba(0, 0, 0, .03)',
                            itemOpacity: 1,
                          },
                        },
                      ],
                    },
                  ]}
                  theme={{
                    axis: {
                      domain: {
                        line: {
                          stroke: colors.grey[100],
                        },
                      },
                      legend: {
                        text: {
                          fill: colors.grey[100],
                        },
                      },
                      ticks: {
                        line: {
                          stroke: colors.grey[100],
                          strokeWidth: 1,
                        },
                        text: {
                          fill: colors.grey[100],
                        },
                      },
                    },
                    legends: {
                      text: {
                        fill: colors.grey[100],
                      },
                    },
                    tooltip: {
                      container: {
                        color: colors.primary[500],
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: colors.primary[400] },
        }}
      >
        <DialogTitle
          sx={{
            color: 'black',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {detailTitle}
          <IconButton onClick={() => setDetailDialogOpen(false)} sx={{ color: 'black' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TableContainer
            component={Paper}
            sx={{ backgroundColor: colors.primary[400], maxHeight: 400 }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      color: 'white',
                      backgroundColor: colors.primary[500],
                      fontWeight: 'bold',
                    }}
                  >
                    E-Mail
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      backgroundColor: colors.primary[500],
                      fontWeight: 'bold',
                    }}
                  >
                    Anmeldedatum
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      backgroundColor: colors.primary[500],
                      fontWeight: 'bold',
                    }}
                  >
                    Erste Webinar-Registrierung
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      backgroundColor: colors.primary[500],
                      fontWeight: 'bold',
                    }}
                  >
                    Tage bis Registrierung
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      backgroundColor: colors.primary[500],
                      fontWeight: 'bold',
                    }}
                  >
                    Produkt
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      backgroundColor: colors.primary[500],
                      fontWeight: 'bold',
                    }}
                  >
                    Webinar Name
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      backgroundColor: colors.primary[500],
                      fontWeight: 'bold',
                    }}
                  >
                    Affiliate
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      backgroundColor: colors.primary[500],
                      fontWeight: 'bold',
                    }}
                  >
                    Client
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailData.map((row, index) => (
                  <TableRow key={`${row.email}-${row.webinar_id}-${index}`}>
                    <TableCell sx={{ color: 'black' }}>{row.email}</TableCell>
                    <TableCell sx={{ color: 'black' }}>
                      {new Date(row.signup_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell sx={{ color: 'black' }}>
                      {row.first_registration_date
                        ? new Date(row.first_registration_date).toLocaleDateString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ color: 'black' }}>{row.days_to_register}</TableCell>
                    <TableCell sx={{ color: 'black' }}>{row.product_name}</TableCell>
                    <TableCell sx={{ color: 'black' }}>{row.webinar_name}</TableCell>
                    <TableCell sx={{ color: 'black' }}>{row.affiliate || 'N/A'}</TableCell>
                    <TableCell sx={{ color: 'black' }}>{row.client || 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WebinarUserVerhalten;
